/**
 * Team Time Off Calendar — manager view of vacation + sick days.
 *
 * Layout: employees run vertically (one row per name), dates fan out
 * horizontally (one narrow column per day of the selected month). Each
 * cell is color-coded — amber for vacation, rose for sick, washed-out
 * variant for pending approval. Left column shows per-employee yearly
 * totals; right rail surfaces pending requests so admin/pm_lead can
 * approve in one click.
 *
 * Date columns are intentionally narrow (~32px) so a full month fits on
 * one screen; weekends and today get column-wide background tints.
 *
 * Data via GET /api/leave-requests/calendar/feed?year=YYYY (one call).
 *
 * marker: team-calendar-names-rows-v1
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plane,
  Stethoscope,
  PartyPopper,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const APPROVE_ROLES = ["admin", "pm_lead"];

// Local-TZ safe parser for "YYYY-MM-DD" strings (avoids UTC-midnight off-by-one).
const parseDate = (s) => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const isWeekend = (d) => {
  const dow = d.getDay();
  return dow === 0 || dow === 6;
};

const monthRange = (year, month) => {
  // month is 0-indexed
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const days = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }
  return days;
};

const cellClass = (entry) => {
  // entry = { type, status } | null
  if (!entry) return "";
  const isApproved = entry.status === "approved";
  if (entry.type === "vacation") {
    return isApproved
      ? "bg-amber-500/40 border-amber-400/60"
      : "bg-amber-500/15 border-amber-500/30 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(245,158,11,0.25)_3px,rgba(245,158,11,0.25)_6px)]";
  }
  if (entry.type === "sick") {
    return isApproved
      ? "bg-rose-500/40 border-rose-400/60"
      : "bg-rose-500/15 border-rose-500/30 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(244,63,94,0.25)_3px,rgba(244,63,94,0.25)_6px)]";
  }
  if (entry.type === "holiday") {
    // Company-wide day off — distinct indigo so it doesn't read as vacation.
    return "bg-indigo-500/35 border-indigo-400/50";
  }
  return "";
};

const TYPE_LABEL = { vacation: "Vacation", sick: "Sick leave", holiday: "Company day off" };

const TeamCalendarPage = () => {
  const { user } = useAuth();
  const canApprove = APPROVE_ROLES.includes(user?.role);

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth()); // 0-indexed
  const [feed, setFeed] = useState({ year, employees: [], requests: [], usage: {} });
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState({}); // requestId → text

  // Filters — the calendar is company-wide now, so scanning it needs narrowing:
  // by absence type, by person, and "only show who's actually out this month".
  // backlog 46bb95e0
  const [typeFilter, setTypeFilter] = useState("all"); // all | vacation | sick | holiday
  const [empQuery, setEmpQuery] = useState("");
  const [onlyAbsent, setOnlyAbsent] = useState(false);

  // Admin "mark leave" dialog — file (and auto-approve) leave for any employee.
  const emptyMark = { employee_id: "", type: "sick", start_date: "", end_date: "", reason: "" };
  const [markOpen, setMarkOpen] = useState(false);
  const [markForm, setMarkForm] = useState(emptyMark);
  const [markSaving, setMarkSaving] = useState(false);

  // "Company day off" dialog — one action marks a day off for the whole team.
  const emptyHoliday = { start_date: "", end_date: "", name: "" };
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [holidayForm, setHolidayForm] = useState(emptyHoliday);
  const [holidaySaving, setHolidaySaving] = useState(false);

  const fetchFeed = async (forYear) => {
    setLoading(true);
    try {
      const res = await api.get("/leave-requests/calendar/feed", { params: { year: forYear } });
      setFeed(res.data || { year: forYear, employees: [], requests: [], usage: {} });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeed(year); }, [year]);

  // Build employee_id → ISO date → entry (latest non-cancelled per cell).
  const cellMap = useMemo(() => {
    const map = {};
    for (const r of feed.requests || []) {
      if (!r.employee_id) continue;
      if (typeFilter !== "all" && r.type !== typeFilter) continue;
      const start = parseDate(r.start_date);
      const end = parseDate(r.end_date);
      if (!start || !end) continue;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = toIso(d);
        const empMap = map[r.employee_id] || (map[r.employee_id] = {});
        // Approved beats pending; vacation/sick stay distinct.
        const existing = empMap[iso];
        if (!existing || (existing.status !== "approved" && r.status === "approved")) {
          empMap[iso] = { id: r.id, type: r.type, status: r.status };
        }
      }
    }
    return map;
  }, [feed, typeFilter]);

  const monthDays = useMemo(() => monthRange(year, month), [year, month]);
  const pending = useMemo(() => (feed.requests || []).filter((r) => r.status === "pending"), [feed]);
  const employees = feed.employees || [];

  const visibleEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    return employees.filter((emp) => {
      if (q && !(emp.name || "").toLowerCase().includes(q)) return false;
      if (onlyAbsent) {
        const days = cellMap[emp.id];
        if (!days || !monthDays.some((d) => days[toIso(d)])) return false;
      }
      return true;
    });
  }, [employees, empQuery, onlyAbsent, cellMap, monthDays]);

  const filtersActive = typeFilter !== "all" || empQuery.trim() !== "" || onlyAbsent;

  const stepMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m);
    if (y !== year) setYear(y);
  };

  const submitMark = async () => {
    if (!markForm.employee_id || !markForm.start_date || !markForm.end_date) {
      toast.error("Pick an employee and a date range");
      return;
    }
    if (markForm.end_date < markForm.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setMarkSaving(true);
    try {
      // auto_approve so the mark takes effect immediately (admin is the authority).
      await api.post("/leave-requests", { ...markForm, auto_approve: true });
      toast.success("Leave recorded");
      setMarkOpen(false);
      setMarkForm(emptyMark);
      await fetchFeed(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record leave");
    } finally {
      setMarkSaving(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      await api.put(`/leave-requests/${id}`, {
        status,
        review_notes: reviewNotes[id] || null,
      });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      setReviewNotes((p) => { const n = { ...p }; delete n[id]; return n; });
      await fetchFeed(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const submitHoliday = async () => {
    if (!holidayForm.start_date || !holidayForm.end_date) {
      toast.error("Pick a date range");
      return;
    }
    if (holidayForm.end_date < holidayForm.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setHolidaySaving(true);
    try {
      const res = await api.post("/leave-requests/company-holiday", holidayForm);
      toast.success(`Day off set for ${res.data?.created ?? 0} employees` +
        (res.data?.skipped ? ` (${res.data.skipped} already off)` : ""));
      setHolidayOpen(false);
      setHolidayForm(emptyHoliday);
      await fetchFeed(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set company day off");
    } finally {
      setHolidaySaving(false);
    }
  };

  // Approver clicks a filled cell → cancel that booking. A company holiday is
  // removed for everyone at once; a personal vacation/sick request is cancelled
  // whole (the entry.id spans its full date range). backlog 0ea0a554 / 6fa4dbdb.
  const cancelEntry = async (emp, iso, entry) => {
    if (!canApprove || !entry) return;
    try {
      if (entry.type === "holiday") {
        if (!window.confirm(`Remove the company day off on ${iso} for everyone?`)) return;
        await api.delete("/leave-requests/company-holiday", { params: { start_date: iso, end_date: iso } });
        toast.success("Company day off removed");
      } else {
        const req = (feed.requests || []).find((r) => r.id === entry.id);
        const range = req && req.start_date !== req.end_date
          ? `${req.start_date} → ${req.end_date}`
          : iso;
        if (!window.confirm(`Cancel ${emp.name}'s ${entry.type} (${range})?`)) return;
        await api.delete(`/leave-requests/${entry.id}`);
        toast.success("Booking cancelled");
      }
      await fetchFeed(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <CalendarIcon className="h-7 w-7 text-sky-400" />
            Team Time Off
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Vacation and sick leave across the team — {year}
            {!canApprove && (
              <span className="text-zinc-600"> · read-only</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canApprove && (
            <>
              <Button
                variant="outline"
                className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 h-9"
                onClick={() => { setHolidayForm(emptyHoliday); setHolidayOpen(true); }}
                title="Mark a day off for the whole team at once"
              >
                <CalendarIcon className="h-4 w-4 mr-1" />
                Company day off
              </Button>
              <Button
                className="bg-sky-600 hover:bg-sky-500 text-white h-9 mr-1"
                onClick={() => { setMarkForm(emptyMark); setMarkOpen(true); }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Mark leave
              </Button>
            </>
          )}
          <Button
            size="icon"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => stepMonth(-1)}
            title="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-1.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-200 text-sm min-w-[150px] text-center">
            {MONTHS[month]} {year}
          </div>
          <Button
            size="icon"
            variant="outline"
            className="border-zinc-700 text-zinc-300"
            onClick={() => stepMonth(1)}
            title="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[110px] bg-zinc-900 border-zinc-700 text-zinc-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              {[year - 2, year - 1, year, year + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filters — narrow a company-wide calendar down to the question you
          actually have: "who is off", "is Sofiia around", "just sick days".
          backlog 46bb95e0 */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <Input
            value={empQuery}
            onChange={(e) => setEmpQuery(e.target.value)}
            placeholder="Find a person…"
            className="pl-8 h-9 w-[200px] bg-zinc-900 border-zinc-700 text-zinc-200 text-sm"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[170px] h-9 bg-zinc-900 border-zinc-700 text-zinc-200 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="all">All absences</SelectItem>
            <SelectItem value="vacation">Vacation only</SelectItem>
            <SelectItem value="sick">Sick leave only</SelectItem>
            <SelectItem value="holiday">Company days off</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          onClick={() => setOnlyAbsent((v) => !v)}
          className={cn(
            "h-9 text-sm",
            onlyAbsent
              ? "border-sky-500/50 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20"
              : "border-zinc-700 text-zinc-400",
          )}
          title="Hide everyone who is in the office all month"
        >
          Only who's off
        </Button>
        {filtersActive && (
          <>
            <span className="text-xs text-zinc-500">
              {visibleEmployees.length} of {employees.length} people
            </span>
            <Button
              variant="ghost"
              onClick={() => { setTypeFilter("all"); setEmpQuery(""); setOnlyAbsent(false); }}
              className="h-9 text-xs text-zinc-500 hover:text-zinc-200"
            >
              Clear
            </Button>
          </>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-amber-500/40 border border-amber-400/60" /> Vacation (approved)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-amber-500/15 border border-amber-500/30 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(245,158,11,0.25)_3px,rgba(245,158,11,0.25)_6px)]" /> Vacation (pending)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-rose-500/40 border border-rose-400/60" /> Sick (approved)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-rose-500/15 border border-rose-500/30 [background-image:repeating-linear-gradient(45deg,transparent,transparent_3px,rgba(244,63,94,0.25)_3px,rgba(244,63,94,0.25)_6px)]" /> Sick (pending)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm bg-indigo-500/35 border border-indigo-400/50" /> Company day off
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Matrix */}
        <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
            ) : employees.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No active employees.</div>
            ) : visibleEmployees.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">
                {onlyAbsent && !empQuery.trim()
                  ? `Nobody is off in ${MONTHS[month]}.`
                  : "No one matches these filters."}
              </div>
            ) : (
              <div className="overflow-auto max-h-[calc(100vh-280px)]">
                <table className="text-xs border-collapse">
                  <thead className="sticky top-0 z-10 bg-zinc-900">
                    <tr>
                      <th className="text-left p-2 text-zinc-400 font-medium border-b border-zinc-800 sticky left-0 bg-zinc-900 z-20 min-w-[200px]">
                        Employee
                      </th>
                      {monthDays.map((d) => {
                        const iso = toIso(d);
                        const weekend = isWeekend(d);
                        const isToday = iso === toIso(new Date());
                        return (
                          <th
                            key={iso}
                            className={cn(
                              "p-1 text-center font-medium border-b border-zinc-800 w-8 min-w-[32px]",
                              weekend ? "bg-zinc-950/40 text-zinc-500" : "text-zinc-300",
                              isToday && "bg-orange-500/10 text-orange-300",
                            )}
                            title={iso}
                          >
                            <div className="font-mono leading-none">{String(d.getDate()).padStart(2, "0")}</div>
                            <div className="text-[9px] text-zinc-500 font-normal leading-none mt-0.5">
                              {["S", "M", "T", "W", "T", "F", "S"][d.getDay()]}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEmployees.map((emp) => {
                      const u = feed.usage?.[emp.id] || {};
                      return (
                        <tr key={emp.id} className="hover:bg-zinc-900/40">
                          <td className="p-2 border-b border-zinc-800/60 sticky left-0 z-10 bg-zinc-900 min-w-[200px]">
                            <div className="text-zinc-200 font-medium truncate" title={emp.name}>{emp.name}</div>
                            {/* Three numbers, not one: a company day off is not
                                leave the person spent, and it no longer counts
                                against their vacation. backlog d3d32706 */}
                            <div className="text-[10px] text-zinc-500 font-normal flex items-center gap-2 mt-0.5">
                              <span className="inline-flex items-center gap-0.5" title="Vacation days used this year (company days off excluded)">
                                <Plane className="h-2.5 w-2.5 text-amber-400" />
                                {u.vacation || 0}
                              </span>
                              <span className="inline-flex items-center gap-0.5" title="Sick days used this year">
                                <Stethoscope className="h-2.5 w-2.5 text-rose-400" />
                                {u.sick || 0}
                              </span>
                              <span className="inline-flex items-center gap-0.5" title="Company days off this year — same for everyone, not charged to anyone's vacation">
                                <PartyPopper className="h-2.5 w-2.5 text-indigo-400" />
                                {u.holiday || 0}
                              </span>
                            </div>
                          </td>
                          {monthDays.map((d) => {
                            const iso = toIso(d);
                            const weekend = isWeekend(d);
                            const isToday = iso === toIso(new Date());
                            const entry = cellMap[emp.id]?.[iso] || null;
                            return (
                              <td
                                key={emp.id + iso}
                                className={cn(
                                  "border-b border-zinc-800/60 border-l border-l-zinc-900 h-7 w-8 min-w-[32px] p-0",
                                  weekend && !entry && "bg-zinc-950/40",
                                  isToday && !entry && "bg-orange-500/5",
                                )}
                              >
                                {entry ? (
                                  <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div
                                          className={cn("h-full w-full border", cellClass(entry), canApprove && "cursor-pointer")}
                                          onClick={() => cancelEntry(emp, iso, entry)}
                                        />
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-zinc-800 border-zinc-700 text-xs">
                                        {emp.name} · {iso} · {TYPE_LABEL[entry.type] || entry.type} · {entry.status}
                                        {canApprove && (
                                          <span className="block text-[10px] text-zinc-400 mt-0.5">Click to cancel</span>
                                        )}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right rail: pending approvals */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-400" />
              Pending ({pending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[calc(100vh-280px)] overflow-auto">
            {pending.length === 0 ? (
              <div className="text-xs text-zinc-500">Nothing waiting for review.</div>
            ) : (
              pending.map((r) => {
                const TypeIcon = r.type === "vacation" ? Plane : Stethoscope;
                return (
                  <div key={r.id} className="p-3 rounded border border-zinc-800 bg-zinc-950/40 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-zinc-200 font-medium truncate">{r.employee_name}</div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          r.type === "vacation"
                            ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
                            : "bg-rose-500/15 text-rose-300 border-rose-500/30",
                        )}
                      >
                        <TypeIcon className="h-3 w-3 mr-1" />
                        {r.type}
                      </Badge>
                    </div>
                    <div className="text-xs text-zinc-400">
                      {r.start_date === r.end_date
                        ? `${r.start_date} · ${r.days_count}d`
                        : `${r.start_date} → ${r.end_date} · ${r.days_count}d`}
                    </div>
                    {r.reason && <div className="text-xs text-zinc-500 italic">"{r.reason}"</div>}
                    {canApprove ? (
                      <>
                        <Textarea
                          value={reviewNotes[r.id] || ""}
                          onChange={(e) =>
                            setReviewNotes((p) => ({ ...p, [r.id]: e.target.value }))
                          }
                          placeholder="Note (optional)"
                          className="bg-zinc-900 border-zinc-700 text-zinc-200 text-xs min-h-[40px]"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs"
                            onClick={() => handleAction(r.id, "approved")}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-zinc-700 text-zinc-300 h-7 text-xs hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300"
                            onClick={() => handleAction(r.id, "rejected")}
                          >
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-[10px] text-zinc-500">
                        Awaiting admin / PM Lead approval.
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin: mark leave for any employee (auto-approved) */}
      <Dialog open={markOpen} onOpenChange={setMarkOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Plus className="h-5 w-5 text-sky-400" />
              Mark leave for an employee
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Employee *</Label>
              <Select
                value={markForm.employee_id}
                onValueChange={(v) => setMarkForm((p) => ({ ...p, employee_id: v }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue placeholder="Pick an employee" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 max-h-72">
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-zinc-50">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Type *</Label>
              <Select
                value={markForm.type}
                onValueChange={(v) => setMarkForm((p) => ({ ...p, type: v }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="sick" className="text-zinc-50">Sick leave</SelectItem>
                  <SelectItem value="vacation" className="text-zinc-50">Vacation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">From *</Label>
                <Input
                  type="date"
                  value={markForm.start_date}
                  onChange={(e) => setMarkForm((p) => ({
                    ...p,
                    start_date: e.target.value,
                    end_date: p.end_date && p.end_date >= e.target.value ? p.end_date : e.target.value,
                  }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">To *</Label>
                <Input
                  type="date"
                  value={markForm.end_date}
                  min={markForm.start_date || undefined}
                  onChange={(e) => setMarkForm((p) => ({ ...p, end_date: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Reason (optional)</Label>
              <Input
                value={markForm.reason}
                onChange={(e) => setMarkForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder="e.g. Sick note received"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              onClick={() => setMarkOpen(false)}
              disabled={markSaving}
            >
              Cancel
            </Button>
            <Button
              className="bg-sky-600 hover:bg-sky-500 text-white"
              onClick={submitMark}
              disabled={markSaving}
            >
              {markSaving ? "Saving…" : "Record leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company day off — one action marks a day off for the whole team */}
      <Dialog open={holidayOpen} onOpenChange={setHolidayOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-indigo-400" />
              Company day off for everyone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-zinc-500">
              Marks the selected dates as a day off for every on-payroll employee at once.
              Anyone already on leave those days is left untouched.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">From *</Label>
                <Input
                  type="date"
                  value={holidayForm.start_date}
                  onChange={(e) => setHolidayForm((p) => ({
                    ...p,
                    start_date: e.target.value,
                    end_date: p.end_date && p.end_date >= e.target.value ? p.end_date : e.target.value,
                  }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">To *</Label>
                <Input
                  type="date"
                  value={holidayForm.end_date}
                  min={holidayForm.start_date || undefined}
                  onChange={(e) => setHolidayForm((p) => ({ ...p, end_date: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">Name (optional)</Label>
              <Input
                value={holidayForm.name}
                onChange={(e) => setHolidayForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Independence Day"
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300"
              onClick={() => setHolidayOpen(false)}
              disabled={holidaySaving}
            >
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
              onClick={submitHoliday}
              disabled={holidaySaving}
            >
              {holidaySaving ? "Saving…" : "Set day off"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamCalendarPage;
